import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Sparkles, ShieldCheck } from "lucide-react";

const ROLES = [
  { value: "both", label: "Both", desc: "Post & run errands" },
  { value: "requester", label: "Requester", desc: "I post errands" },
  { value: "runner", label: "Runner", desc: "I run errands" },
];

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [form, setForm] = useState({ display_name: "", email: "", password: "", role: "both", location: "" });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const updName = (e) => setForm({ ...form, display_name: e.target.value.replace(/\s/g, "").slice(0, 15) });

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin && !agreed) {
      toast.error("Please agree to the Terms & Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
      } else {
        await register(form);
      }
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      navigate("/feed");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-5 py-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="w-full max-w-md bg-white rounded-3xl border border-gray-100 shadow-soft p-8">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <span className="w-9 h-9 rounded-2xl bg-primary flex items-center justify-center text-white"><Sparkles size={18} /></span>
          <span className="font-heading font-extrabold text-xl tracking-tight">Errandell</span>
        </Link>
        <h1 className="font-heading font-extrabold text-3xl text-[#1A1D20] mb-1">
          {isLogin ? "Welcome back" : "Create your account"}
        </h1>
        <p className="text-sm text-[#6B7280] mb-6">
          {isLogin ? "Log in to see errands near you." : "Join your local community in a minute."}
        </p>

        <form onSubmit={submit} className="space-y-4">
          {!isLogin && (
            <div>
              <Label className="font-semibold text-sm">Display name <span className="text-[#9CA3AF] font-normal">(optional)</span></Label>
              <Input data-testid="auth-name-input" value={form.display_name} onChange={updName} maxLength={15} placeholder="Leave blank for a random alias"
                className="mt-1.5 rounded-2xl h-12 border-gray-200" />
              <p className="text-[11px] text-[#9CA3AF] mt-1 flex items-center gap-1">
                <ShieldCheck size={12} /> Up to 15 characters, no spaces. We never ask for your real name.
              </p>
            </div>
          )}
          <div>
            <Label className="font-semibold text-sm">Email</Label>
            <Input data-testid="auth-email-input" type="email" value={form.email} onChange={upd("email")} required placeholder="you@campus.edu"
              className="mt-1.5 rounded-2xl h-12 border-gray-200" />
          </div>
          <div>
            <Label className="font-semibold text-sm">Password</Label>
            <Input data-testid="auth-password-input" type="password" value={form.password} onChange={upd("password")} required placeholder="••••••••"
              className="mt-1.5 rounded-2xl h-12 border-gray-200" />
            {isLogin && (
              <div className="text-right mt-1.5">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-primary hover:underline">Forgot password?</Link>
              </div>
            )}
          </div>
          {!isLogin && (
            <>
              <div>
                <Label className="font-semibold text-sm">Neighborhood / Campus</Label>
                <Input data-testid="auth-location-input" value={form.location} onChange={upd("location")} placeholder="NYU · Greenwich Village"
                  className="mt-1.5 rounded-2xl h-12 border-gray-200" />
              </div>
              <div>
                <Label className="font-semibold text-sm mb-1.5 block">I want to…</Label>
                <div className="grid grid-cols-3 gap-2">
                  {ROLES.map((r) => (
                    <button type="button" key={r.value} data-testid={`role-${r.value}`}
                      onClick={() => setForm({ ...form, role: r.value })}
                      className={`rounded-2xl border-2 p-3 text-left transition-all ${form.role === r.value ? "border-primary bg-primary/5" : "border-gray-200 hover:border-gray-300"}`}>
                      <div className="font-bold text-sm text-[#1A1D20]">{r.label}</div>
                      <div className="text-[10px] text-[#6B7280] leading-tight">{r.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          <Button data-testid="auth-submit-btn" type="submit" disabled={loading}
            className="w-full rounded-full h-12 font-bold bg-primary hover:bg-[#FF8D70] active:scale-95 transition-transform">
            {loading ? "Please wait…" : isLogin ? "Log in" : "Create account"}
          </Button>
        </form>

        {!isLogin && (
          <label className="flex items-start gap-2.5 mt-4 cursor-pointer">
            <Checkbox data-testid="auth-terms-checkbox" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" />
            <span className="text-xs text-[#6B7280] leading-snug">
              I agree to the{" "}
              <Link to="/terms" target="_blank" data-testid="auth-terms-link" className="font-semibold text-primary hover:underline">Terms & Privacy Policy</Link>.
            </span>
          </label>
        )}

        <p className="text-center text-sm text-[#6B7280] mt-6">
          {isLogin ? "New here? " : "Already have an account? "}
          <Link data-testid="auth-switch-link" to={isLogin ? "/register" : "/login"} className="font-bold text-primary hover:underline">
            {isLogin ? "Create an account" : "Log in"}
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
